/// <amd-module name="Artifacts/FeedSettings/components/AddUpstreamForm" />
import {
  suggestedUpstreamName,
  suggestedUpstreamNameForAadLocator,
} from "Artifacts/FeedSettings/helpers/UpstreamHelpers";
import {
  AddUpstreamFormAction,
  AddUpstreamFormState,
  AddUpstreamType,
  HiddenFormValidationErrors,
  IAddUpstreamsErrors,
  IAddUpstreamsSelections,
} from "Artifacts/FeedSettings/hooks/AddUpstreamForm";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IUpstreamSource } from "Artifacts/FeedSettings/types";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { IProtocol } from "Artifacts/Root/protocols/types";
import { Feed, FeedView } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as StringUtils from "VSS/Core/Util/String";
import { usePageContext } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { TextField } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";

export interface IAddUpstreamFormProps {
  selections: IAddUpstreamsSelections;
  errors: IAddUpstreamsErrors;
  sameCollectionFeedListComponentState: ComponentState;
  feeds: IListBoxItem<Feed>[];
  formState: AddUpstreamFormState;
  type: AddUpstreamType;
  dispatch: React.Dispatch<AddUpstreamFormAction>;
  protocols: IProtocol[];
  existingUpstreams?: IUpstreamSource[];
  publicUpstreams: IListBoxItem<IUpstreamSource | null>[];
  feedViews: IListBoxItem<FeedView>[];
  serviceConnections: IListBoxItem<ServiceEndpoint>[];
  serviceConnectionsComponentState: ComponentState;
  isServiceConnectionsFeatureEnabled: boolean;
}

const AddUpstreamForm: React.FC<IAddUpstreamFormProps> = ({
  selections,
  errors,
  type,
  dispatch,
  formState,
  protocols,
  existingUpstreams,
  publicUpstreams,
  sameCollectionFeedListComponentState,
  feeds,
  feedViews,
  serviceConnections,
  serviceConnectionsComponentState,
  isServiceConnectionsFeatureEnabled,
}) => {
  const pageContext = usePageContext();
  const supportedCustomPublicProtocols = protocols
    .filter((p) => p.getUpstreamingProperties(pageContext).isCustomPublicUpstreamsSupported)
    .map((protocol) => protocol.id);

  // Suggested improvement: encapsulate the below in a hook
  /**
   * Determine whether an upstream source with the same target as the current selection
   * exists. Return the name of existing source(s).
   * For upstreams that target the same feed+view, all protocols must share the
   * same name.
   * @returns string feedName if source already exists, else undefined
   */
  const alreadyExistingUpstreamName = React.useCallback(() => {
    const selectedFeed = formState.selectedSameOrgFeed;
    const selectedView = formState.selectedView;
    const location = formState.aadLocation;
    // In order to match existing source, we have to have selected one already
    if (selectedFeed && selectedView) {
      return existingUpstreams?.find((upstream) => {
        return (
          upstream.internalUpstreamFeedId === selectedFeed.id &&
          upstream.internalUpstreamViewId === selectedView.id
        );
      })?.name;
    } else if (location) {
      return existingUpstreams?.find((upstream) => {
        return upstream.location === location;
      })?.name;
    }

    return undefined;
  }, [
    formState.selectedSameOrgFeed,
    formState.selectedView,
    formState.aadLocation,
    existingUpstreams,
  ]);

  React.useEffect(() => {
    const existingName = alreadyExistingUpstreamName()
    if (existingName) {
      dispatch({ type: "name", value: existingName});
      return;
    }

    const selectedFeed = formState.selectedSameOrgFeed;
    const selectedView = formState.selectedView;
    if (selectedFeed && selectedView) {
      dispatch({ type: "name", value: suggestedUpstreamName(selectedFeed, selectedView) });
      return;
    }

    const aadLocation = formState.aadLocation;
    if (aadLocation && !errors.aadLocationError) {
      dispatch({ type: "name", value: suggestedUpstreamNameForAadLocator(aadLocation) });
      return;
    }
  }, [
    formState.selectedSameOrgFeed,
    formState.selectedView,
    formState.aadLocation,
    errors.aadLocationError,
    existingUpstreams,
    alreadyExistingUpstreamName,
    dispatch,
  ]);

  return (
    <div className="flex flex-column add-upstream-form">
      {type === AddUpstreamType.Public && (
        <>
          <FormItem
            className="add-upstream-form-item required"
            label={Res.AddUpstreamPanel_PublicUpstream_PublicSource_Label}
          >
            <Dropdown
              required={true}
              items={publicUpstreams}
              selection={selections.publicSourceDropdown}
              placeholder={Res.AddUpstreamPanel_PublicUpstream_PublicSource_Placeholder}
              onSelect={(_event, item) => {
                dispatch({ type: "selectedPublicSource", value: item?.data || null });
                dispatch({ type: "publicLocation", value: item?.data?.location || "" });
                dispatch({ type: "name", value: item?.data?.name || "" });

                // Prepopulate package type for custom public upstream.
                if (item?.id === "custom") {
                  dispatch({ type: "protocol", value: supportedCustomPublicProtocols[0] });
                  selections.publicPackageTypeDropdown.select(0);
                }
              }}
            />
          </FormItem>
          {formState.selectedPublicSource == null &&
            selections.publicSourceDropdown.selectedCount > 0 && (
              <>
                <FormItem
                  className="add-upstream-form-item required"
                  error={errors.publicLocationError != null}
                  message={errors.publicLocationError?.description}
                  label={Res.AddUpstreamPanel_PublicUpstream_UpstreamLocation_Label}
                >
                  <TextField
                    value={formState.publicLocation}
                    disabled={formState.selectedPublicSource != null}
                    required
                    onChange={(_event, value) => dispatch({ type: "publicLocation", value })}
                  />
                </FormItem>
                <FormItem
                  className="add-upstream-form-item required"
                  label={Res.AddUpstreamPanel_PublicUpstream_UpstreamProtocol_Label}
                >
                  <Dropdown
                    required={true}
                    placeholder={Res.AddUpstreamPanel_PublicUpstream_UpstreamProtocol_Placeholder}
                    items={supportedCustomPublicProtocols}
                    selection={selections.publicPackageTypeDropdown}
                    disabled={formState.selectedPublicSource != null}
                    onSelect={(_event, value) => dispatch({ type: "protocol", value: value.id })}
                  />
                </FormItem>
              </>
            )}
        </>
      )}
      {type === AddUpstreamType.CollectionUpstream && (
        <DefaultLoader state={sameCollectionFeedListComponentState}>
          {feeds.length === 0 && (
            <MessageCard
              className="flex-self-stretch margin-bottom-8"
              severity={MessageCardSeverity.Warning}
            >
              {Res.AddUpstreamPanel_SameCollectionFeed_NoAvailableFeeds_Message}
            </MessageCard>
          )}
          <FormItem
            className="add-upstream-form-item required"
            label={Res.AddUpstreamPanel_SameCollectionFeed_Feed_Label}
          >
            <Dropdown
              items={feeds}
              disabled={feeds.length === 0}
              selection={selections.sameOrgFeedDropdown}
              placeholder={Res.AddUpstreamPanel_SameCollectionFeed_Feed_Placeholder}
              filterPlaceholderText={Res.AddUpstreamPanel_SameCollectionFeed_FeedSearch_Placeholder}
              onSelect={(_event, item) => {
                dispatch({ type: "selectedOrgFeed", value: item.data || null });
              }}
              required={true}
            />
          </FormItem>
          {feedViews.length > 0 && feedViews?.every((item) => item.disabled === true) && (
            <MessageCard className="flex-self-stretch" severity={MessageCardSeverity.Warning}>
              {Res.AddUpstreamPanel_SameCollectionFeed_NoAvailableViews_Message}
            </MessageCard>
          )}
          <FormItem
            className="add-upstream-form-item required"
            label={Res.AddUpstreamPanel_SameCollectionFeed_View_Label}
          >
            <Dropdown
              items={feedViews}
              selection={selections.feedViewDropdown}
              onSelect={(_event, item) => {
                dispatch({ type: "selectedView", value: item.data || null });
              }}
              placeholder={Res.AddUpstreamPanel_SameCollectionFeed_View_Placeholder}
              disabled={feedViews == null || feedViews.length === 0}
              required={true}
            />
          </FormItem>
          <FormItem
            className="add-upstream-form-item required"
            label={Res.AddUpstreamPanel_Protocols_Selection}
          >
            <div className="visibility-hidden" id={getSafeId("package-type-description")}>
              {Res.UpstreamsTab_PackageTypeRequired}
            </div>
            <div className="flex flex-row">
              {protocols
                .filter(
                  (protocol) =>
                    protocol.getUpstreamingProperties(pageContext).isCollectionUpstreamsSupported,
                )
                .map((protocol) => (
                  <Checkbox
                    ariaDescribedBy={"package-type-description"}
                    key={protocol.id}
                    onChange={(_event, checked) =>
                      dispatch({
                        type: checked ? "appendProtocol" : "removeProtocol",
                        value: protocol.id,
                      })
                    }
                    checked={formState.protocols.indexOf(protocol.id) !== -1}
                    disabled={
                      // Requirements for checkbox to be enabled:
                      // A feed must be selected
                      formState.selectedSameOrgFeed == null ||
                      // A view must be selected
                      formState.selectedView == null ||
                      // No upstreams for this feed view and protocol in the list already
                      existingUpstreams?.some((upstream) => {
                        return (
                          upstream.internalUpstreamFeedId === formState.selectedSameOrgFeed?.id &&
                          upstream.internalUpstreamViewId === formState.selectedView?.id &&
                          StringUtils.equals(upstream.protocol, protocol.id, true)
                        );
                      })
                    }
                    label={protocol.name}
                  />
                ))}
            </div>
          </FormItem>
        </DefaultLoader>
      )}
      {type === AddUpstreamType.AadUpstream && (
        <>
          <FormItem
            className="add-upstream-form-item required"
            label={Res.AddUpstreamPanel_CrossCollectionFeed_Location_Label}
            error={errors.aadLocationError != null}
            message={
              errors.aadLocationError?.description !==
                HiddenFormValidationErrors.UserInputNotStartedError &&
              errors.aadLocationError?.description
            }
          >
            <TextField
              placeholder={Res.AddUpstreamPanel_ValidationError_Location_Example}
              value={formState.aadLocation}
              required
              onChange={(_event, value) => dispatch({ type: "aadLocation", value })}
            />
          </FormItem>
          <FormItem
            className="add-upstream-form-item required"
            label={Res.AddUpstreamPanel_Protocols_Selection}
          >
            <div className="visibility-hidden" id={getSafeId("package-type-description")}>
              {Res.UpstreamsTab_PackageTypeRequired}
            </div>
            <div className="flex flex-row">
              {protocols
                .filter(
                  (protocol) =>
                    protocol.getUpstreamingProperties(pageContext).isAadUpstreamsSupported,
                )
                .map((protocol) => (
                  <Checkbox
                    ariaDescribedBy="package-type-description"
                    key={protocol.id}
                    onChange={(_event, checked) =>
                      dispatch({
                        type: checked ? "appendProtocol" : "removeProtocol",
                        value: protocol.id,
                      })
                    }
                    checked={formState.protocols.indexOf(protocol.id) !== -1}
                    label={protocol.name}
                  />
                ))}
            </div>
          </FormItem>
          {isServiceConnectionsFeatureEnabled && (
            <DefaultLoader state={serviceConnectionsComponentState}>
              <FormItem
                className="add-upstream-form-item"
                label={Res.AddUpstreamPanel_CrossCollectionFeed_ServiceConnection_Label}
              >
                <div className="flex flex-row">
                  <Checkbox
                    onChange={(_event, value) =>
                      dispatch({ type: "isServiceConnectionInUse", value })
                    }
                    checked={formState.isServiceConnectionInUse}
                    label={Res.AddUpstreamPanel_CrossCollectionFeed_UseServiceConnection_Label}
                  />
                </div>
              </FormItem>
              {formState.isServiceConnectionInUse && (
                <>
                  {serviceConnections.length === 0 && (
                    <MessageCard
                      className="flex-self-stretch margin-bottom-8"
                      severity={MessageCardSeverity.Warning}
                    >
                      {Res.AddUpstreamPanel_CrossCollectionFeed_NoServiceConnections_Message}
                    </MessageCard>
                  )}
                  <FormItem className="add-upstream-form-item">
                    <Dropdown
                      items={serviceConnections}
                      onSelect={(_event, item) =>
                        dispatch({ type: "serviceConnectionId", value: item.data?.id || "" })
                      }
                      placeholder={
                        Res.AddUpstreamPanel_CrossCollectionFeed_ServiceConnectionDropdown_Placeholder
                      }
                      disabled={serviceConnections.length === 0}
                    />
                  </FormItem>
                </>
              )}
            </DefaultLoader>
          )}
        </>
      )}
      {// Display upstream name field if upstream type is not public
      (type !== AddUpstreamType.Public ||
        // Or if selected public upstream is a custom public upstream
        (formState.selectedPublicSource == null &&
          selections.publicSourceDropdown.selectedCount > 0)) && (
        <FormItem
          className="add-upstream-form-item required"
          label={Res.AddUpstreamPanel_UpstreamName_Label}
          error={errors.feedNameError != null}
          message={
            errors.feedNameError?.description !==
              HiddenFormValidationErrors.UserInputNotStartedError &&
            errors.feedNameError?.description
          }
        >
          <TextField
            value={formState.name}
            disabled={!!alreadyExistingUpstreamName() || formState.selectedPublicSource != null}
            onChange={(_event, value) => {
              dispatch({ type: "name", value });
            }}
            required
          />
        </FormItem>
      )}
    </div>
  );
};

export { AddUpstreamForm };
